import { z } from "zod";
import { fallback } from "@tanstack/zod-adapter";

/**
 * Standard URL search-param contract for date filtering across OperatorOS.
 *
 * - `start` / `end` are inclusive ISO dates (YYYY-MM-DD) interpreted in the
 *   facility's timezone server-side.
 * - `dateField` is whitelisted per page (passed through to server fn).
 * - `sort` is newest-first by default.
 *
 * Pages compose this with their own status / search / pagination params.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const dateRangeSearchSchema = z.object({
  start: fallback(isoDate.optional(), undefined),
  end: fallback(isoDate.optional(), undefined),
  dateField: fallback(z.string().optional(), undefined),
  sort: fallback(z.enum(["asc", "desc"]).optional(), undefined),
});

export type DateRangeSearch = z.infer<typeof dateRangeSearchSchema>;

export const retainDateRangeKeys = ["start", "end", "dateField", "sort"] as const;

/**
 * Convert URL search-param dates into ISO datetime bounds for server queries.
 * `start` becomes 00:00:00 of that day, `end` becomes 23:59:59.999 of that day.
 */
export function dateRangeToBounds(value: { start?: string; end?: string }): {
  fromIso?: string;
  toIso?: string;
} {
  return {
    fromIso: value.start ? new Date(value.start + "T00:00:00").toISOString() : undefined,
    toIso: value.end ? new Date(value.end + "T23:59:59.999").toISOString() : undefined,
  };
}

/**
 * Server-side input shape — server fns should accept this and apply via
 * `.gte(field, fromIso).lte(field, toIso).order(field, { ascending })`.
 * `dateField` must be whitelisted per server fn (never trust client value).
 */
export const serverDateRangeInput = z.object({
  fromIso: z.string().datetime().optional(),
  toIso: z.string().datetime().optional(),
  sort: z.enum(["asc", "desc"]).optional(),
});